// The three painted terrain bands behind the farm, each a strip repeated with mirrored
// copies, plus what hides the lawn's straight top edge.
//
// Motion: each band slides at its share of the pan (BAND_PARALLAX). The near hedge overlaps
// the lawn's top edge from the front; a row of lawn-green tufts inside the world (so they pan
// and zoom with the lawn) peeks out from under it.
//
// Haze: every far/mid sprite has a twin drawn over it with setTintFill(sky colour) at the
// band's haze alpha — only the band's own pixels get hazed, never the sky around them. A
// screen-wide gradient over the first lawn rows fades the grass into the distance too.
import { bandLight, worldHazeAlpha } from './skyClock.mjs';
import { stripCopies, fringeTufts, tuftOffset, bandBottom, bandScale, bandX, snapPx, splitPx, HAZE_CELLS, BAND_PARALLAX } from './backdropLayout.mjs';
import { ZOOM } from './farmView.mjs';
import { BD, BAND_KEYS } from './backdropAnchors.mjs';
import { HORIZON_MARGIN, LAWN_MARGIN, TILE_H, TILE_W, farmExtent } from './iso.mjs';
import { DEPTH } from './backdropDepth.mjs';
import { GAME_WIDTH } from '../config.js';

// Band bottoms: backdropLayout.bandBottom (the lifts scale with the zoomed hedge).
export const BAND = {
  far: { factor: BAND_PARALLAX.far, depth: DEPTH.far, phase: 311 },
  mid: { factor: BAND_PARALLAX.mid, depth: DEPTH.mid, phase: 97 },
  near: { factor: BAND_PARALLAX.near, depth: DEPTH.near, phase: 0 },   // bottom: nearBandBottom()
};
// Enough copies to cover the screen at any pan: a strip narrower than the screen needs more.
const copiesFor = (w) => Math.ceil(GAME_WIDTH / w) + 2;
const TUFT_KEYS = [0, 1, 2, 3].map((i) => `farm_bd_tuft_${i}`);
const FRINGE_SEED = 20260928;

export function createBands(scene, world) {
  const has = (k) => scene.textures.exists(k);
  const bands = {};
  for (const [name, cfg] of Object.entries(BAND)) {
    const key = BAND_KEYS[name];
    if (!has(key)) continue;
    // One container per band, placed at the band's sub-pixel offset (containers are not
    // pixel-rounded); inside it every copy sits on whole pixels (splitPx). The band glides
    // smoothly as the camera settles instead of stepping a pixel every few frames.
    const box = scene.add.container(0, 0).setScrollFactor(0).setDepth(cfg.depth);
    const mk = () => scene.add.image(0, 0, key).setOrigin(0, 1).setScale(0.5).setVisible(false);
    const n = copiesFor(BD[key].w * bandScale(name, ZOOM.min));   // enough for the smallest it gets
    const imgs = [], veils = [];
    for (let i = 0; i < n; i++) {
      imgs.push(mk()); box.add(imgs[i]);
      if (name !== 'near') { veils.push(mk()); box.add(veils[i]); }   // each veil right over its strip
    }
    bands[name] = { cfg, box, imgs, veils, w: BD[key].w, h: BD[key].h };
  }

  // The tufts: inside the world, above the lawn TileSprite and below the plots; rebuilt when
  // the farm grows (the lawn gets wider).
  const horizonLocal = -HORIZON_MARGIN * TILE_H;
  const tuftKeys = TUFT_KEYS.filter(has);
  let tufts = [];
  let builtFor = -1;
  const rebuildTufts = () => {
    for (const t of tufts) t.destroy();
    tufts = [];
    builtFor = world.size;
    if (!tuftKeys.length) return;
    const x0 = -LAWN_MARGIN * TILE_W;
    const width = farmExtent(world.size).w + 2 * LAWN_MARGIN * TILE_W;
    // Each addAt(…, 1) lands UNDER the previous one, so insert from the lowest tuft up: the
    // nearer (lower) tufts end up drawn over the farther ones.
    const placed = fringeTufts(x0, width, FRINGE_SEED, tuftKeys.length)
      .map((p) => ({ ...p, y: horizonLocal + tuftOffset(p) }))
      .sort((a, b) => b.y - a.y);
    for (const p of placed) {
      const img = scene.add.image(p.x, p.y, tuftKeys[p.variant]).setOrigin(0.5, 1).setScale(0.5);
      world.container.addAt(img, 1);
      tufts.push(img);
    }
  };

  // The distance haze is screen-fixed (just above the world, below the farm light and the
  // near band) so it spans the whole width — inside the world it ended in a hard line at the
  // finite lawn's edge; its height scales with the zoom like the lawn rows it covers.
  const haze = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH.worldHaze);
  let hazeSig = '';
  const drawHaze = (ctx) => {
    const colour = bandLight(ctx.t, 'far').haze;
    const a = Math.round(worldHazeAlpha(ctx.t) * 100) / 100;
    const y = Math.round(ctx.horizonY);
    const h = Math.round(HAZE_CELLS * TILE_H * ctx.zoom);
    const sig = `${colour}|${a}|${y}|${h}`;
    if (sig === hazeSig) return;
    hazeSig = sig;
    haze.clear();
    if (y + h <= 0 || y >= ctx.viewH) return;
    haze.fillGradientStyle(colour, colour, colour, colour, a, a, 0, 0);
    haze.fillRect(0, y, ctx.viewW, h);
  };

  const bottomOf = (name, ctx) => bandBottom(name, ctx.horizonY, ctx.zoom);
  // Where a band sits this frame: its strip offset and bottom, and their sub-pixel fractions
  // (the band container's position; the band objects in backdropFx.js use the same).
  const frame = (name, ctx) => {
    const offset = bandX(name, BAND[name].phase, ctx.panX, ctx.zoom, ctx.viewW / 2, BAND[name].factor);
    const bottom = bottomOf(name, ctx);
    return { offset, bottom, fx: splitPx(offset).frac, fy: splitPx(bottom).frac };
  };

  return {
    bandGeometry(ctx) {
      return Object.fromEntries(Object.keys(BAND).map((n) => {
        const f = frame(n, ctx);
        return [n, { bottom: f.bottom, factor: BAND[n].factor, scale: bandScale(n, ctx.zoom), fx: f.fx, fy: f.fy }];
      }));
    },
    update(ctx) {
      if (world.size !== builtFor) rebuildTufts();
      drawHaze(ctx);
      for (const [name, b] of Object.entries(bands)) {
        const light = bandLight(ctx.t, name);
        // Whole-pixel copy width, so scaled copies still butt exactly.
        const sc = bandScale(name, ctx.zoom);
        const w = Math.max(1, Math.round(b.w * sc));
        const h = Math.max(1, Math.round(b.h * sc));
        // The near band zooms about the middle of the screen (bandX), so both sides grow/shrink evenly.
        const f = frame(name, ctx);
        const copies = stripCopies(f.offset, w, ctx.viewW);
        b.box.setPosition(f.fx, f.fy);
        const bottom = snapPx(f.bottom - f.fy);        // container-local, whole pixels
        b.imgs.forEach((img, i) => {
          const c = copies[i];
          img.setVisible(!!c);
          if (!c) return;
          const x = snapPx(c.x - f.fx);   // whole pixels inside the band: copies never leave a crack
          img.setDisplaySize(w, h).setPosition(x, bottom).setFlipX(c.flip).setTint(light.tint);
          const v = b.veils[i];
          if (v) v.setVisible(light.hazeAlpha > 0.005).setDisplaySize(w, h).setPosition(x, bottom).setFlipX(c.flip)
            .setTintFill(light.haze).setAlpha(light.hazeAlpha);
        });
      }
    },
    destroy() {
      for (const b of Object.values(bands)) b.box.destroy(true);
      for (const t of tufts) t.destroy();
      haze.destroy();
    },
  };
}
